import React, { CSSProperties, useState } from 'react';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useJoinWaitlist } from 'hooks/useJoinWaitlist';
import { useSupportsHover } from 'hooks/useSupportsHover';
import {
  saloonButtonDisabledStyle,
  saloonButtonStyle,
  saloonHoverScaleIn,
  saloonHoverScaleOut
} from 'styles/saloonButtons';
import type { WaitlistGameType } from 'types/waitlist';

import JoinWaitlistFailedAlert from './JoinWaitlistFailedAlert';

type JoinWaitlistButtonProps = {
  gameId: number;
  gameType: WaitlistGameType;
  onJoined?: () => void;
  onError?: (err: Error) => void;
};

/**
 * Primary CTA shown on full tables when the player is not queued. Joining sends
 * no auto buy-in (autoBuyIn: 0); the player sets their buy-in when a seat opens.
 */
const JoinWaitlistButton: React.FC<JoinWaitlistButtonProps> = ({
  gameId,
  gameType,
  onJoined,
  onError
}) => {
  const { join, pending, playerGuid } = useJoinWaitlist();
  const [failedAlertOpen, setFailedAlertOpen] = useState(false);
  const supportsHover = useSupportsHover();

  const handleJoin = async () => {
    const { ok, error } = await join({ gameId, gameType });
    if (ok) {
      onJoined?.();
      return;
    }
    // Don't surface a failure alert for the no-op guard (e.g. missing guid):
    // an error is only present when the engine request actually rejected.
    if (error !== undefined) {
      // Unity parity: TablesView.cs:1763 OnFailedJoinWaitlist shows a
      // ShowAlert(WAITLIST_TITLE, ERROR_MESSAGE, [CLOSE_BUTTON]) on any
      // join-waitlist failure. Render the same alert here before/alongside
      // the onError callback so consumers don't need to duplicate it.
      setFailedAlertOpen(true);
      onError?.(error as Error);
    }
  };

  const disabled = pending || !playerGuid;
  const buttonLabel = pending ? 'Joining Waitlist…' : 'Join Waitlist';

  return (
    <div style={containerStyle}>
      <JoinWaitlistFailedAlert open={failedAlertOpen} onClose={() => setFailedAlertOpen(false)} />
      <button
        aria-label='join-waitlist-button'
        onClick={handleJoin}
        disabled={disabled}
        style={{
          ...saloonButtonStyle,
          ...(disabled ? saloonButtonDisabledStyle : null)
        }}
        {...(supportsHover && !disabled
          ? { onMouseEnter: saloonHoverScaleIn, onMouseLeave: saloonHoverScaleOut }
          : {})}>
        <HourglassTopIcon style={{ fontSize: '1.1rem' }} />
        {buttonLabel}
      </button>
    </div>
  );
};

export default JoinWaitlistButton;

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '48px',
  minWidth: '160px'
};
